import { notification } from "antd";
import {
  allocateOverpayment,
  clearAccountingCache,
} from "../../../../api/accounting";

/**
 * Handles allocation of overpayment to a specific invoice or another overpayment
 *
 * @param {string|number} overpaymentId - ID of the overpayment
 * @param {string|number} targetId - ID of the invoice or overpayment to allocate to
 * @param {number|string} amount - Amount to allocate
 * @param {Array} allocations - Current allocations array
 * @param {Array} items - Current invoices or overpayments array
 * @param {string|number|null} allocationId - ID of existing allocation (if updating)
 * @param {string|number|null} selectedPartner - ID of selected partner
 * @param {Function} setLoadingAllocation - State setter for loading indicator
 * @param {Function} setItems - State setter for invoices or overpayments
 * @param {Function} setAllocations - State setter for allocations
 * @param {Function} fetchOverpayment - Function to refresh overpayment data
 * @param {Function} fetchItems - Function to refresh invoices or overpayments
 * @param {string} allocationType - Type of allocation ('invoice' or 'overpayment')
 * @returns {Promise<boolean>} - Returns true if allocation was successful
 */
export const handleAllocateOverpayment = async (
  overpaymentId,
  targetId,
  amount,
  allocations,
  items,
  allocationId = null,
  selectedPartner,
  setLoadingAllocation,
  setItems,
  setAllocations,
  fetchOverpayment,
  fetchItems,
  allocationType = "invoice"
) => {
  // Find the target item (invoice or overpayment) to get its identifier for messages
  const targetItem = items.find((item) => item.id === targetId);
  const targetIdentifier =
    allocationType === "invoice"
      ? targetItem
        ? targetItem.number
        : targetId
      : targetItem
      ? `Overpayment #${targetItem.id}`
      : targetId;

  // Convert amount to number if it's a string
  const overpaymentAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  // Check if this is a deallocation (amount is zero)
  const isDeallocation = overpaymentAmount === 0;

  // For allocation (not deallocation), validate amount is positive
  if (
    !isDeallocation &&
    (!overpaymentAmount || overpaymentAmount <= 0 || isNaN(overpaymentAmount))
  ) {
    notification.error({
      message: "Validation Error",
      description: "Please enter a valid overpayment amount greater than 0",
    });
    return false;
  }

  // Prevent creating a new allocation with zero amount
  if (isDeallocation && !allocationId) {
    notification.error({
      message: "Validation Error",
      description: "Amount must be greater than 0 to create a new allocation",
    });
    return false;
  }

  // Find the target item to validate against remaining amount
  const itemToAllocate = items.find((item) => item.id === targetId);
  if (!itemToAllocate) {
    notification.error({
      message: "Validation Error",
      description: `${
        allocationType === "invoice" ? "Invoice" : "Overpayment"
      } not found`,
    });
    return false;
  }

  // Prevent allocation to cancelled invoices (only for invoice allocations)
  if (
    !isDeallocation &&
    allocationType === "invoice" &&
    itemToAllocate.status === "cancelled"
  ) {
    notification.error({
      message: "Validation Error",
      description: `Cannot allocate overpayment to cancelled invoice #${
        itemToAllocate.number || targetIdentifier
      }. Cancelled invoices cannot receive allocations.`,
    });
    return false;
  }

  // Only validate remaining amount for new allocations (not deallocations)
  if (!isDeallocation) {
    const remainingAmount = parseFloat(itemToAllocate.remainingAmount);
    const epsilon = 0.001; // Small tolerance for floating point comparison

    // If updating an existing allocation, add its current amount to the remaining amount
    let adjustedRemainingAmount = remainingAmount;
    const existingAllocation =
      allocationType === "invoice"
        ? allocations.find((alloc) => alloc.invoice_id === targetId)
        : allocations.find((alloc) => alloc.target_overpayment_id === targetId);

    if (allocationId && existingAllocation) {
      adjustedRemainingAmount += parseFloat(existingAllocation.amount || 0);
    }

    if (overpaymentAmount > adjustedRemainingAmount + epsilon) {
      notification.error({
        message: "Validation Error",
        description: `Overpayment amount (${overpaymentAmount.toFixed(
          2
        )}) exceeds the remaining balance (${adjustedRemainingAmount.toFixed(
          2
        )}) for ${
          allocationType === "invoice"
            ? `invoice #${itemToAllocate.number}`
            : `overpayment #${itemToAllocate.id}`
        }`,
      });
      return false;
    }
  }

  // Validate overpayment has enough unallocated amount
  if (
    (allocationType === "invoice" && targetItem?.overpayment) ||
    (allocationType === "overpayment" && targetItem && !isDeallocation)
  ) {
    const remainingOverpayment = parseFloat(
      allocationType === "invoice"
        ? targetItem.overpayment.remaining_amount || 0
        : targetItem.remaining_amount || 0
    );

    // If updating an existing allocation, add its current amount to the remaining amount
    let adjustedRemainingOverpayment = remainingOverpayment;
    const existingAllocation =
      allocationType === "invoice"
        ? allocations.find((alloc) => alloc.invoice_id === targetId)
        : allocations.find((alloc) => alloc.target_overpayment_id === targetId);

    if (allocationId && existingAllocation) {
      adjustedRemainingOverpayment += parseFloat(
        existingAllocation.amount || 0
      );
    }

    // Add a small epsilon to handle floating point precision issues
    const epsilon = 0.001;

    if (overpaymentAmount > adjustedRemainingOverpayment + epsilon) {
      notification.error({
        message: "Validation Error",
        description: `Overpayment amount (${overpaymentAmount}) exceeds the remaining overpayment amount (${adjustedRemainingOverpayment.toFixed(
          2
        )})`,
      });
      return false;
    }
  }

  try {
    setLoadingAllocation(true);

    // If this is a deallocation (amount is zero) and we have an allocation ID
    if (isDeallocation && allocationId) {
      if (allocationType === "invoice") {
        // For invoice allocations, use the allocate_overpayment endpoint with amount=0
        const deallocData = {
          overpayment_id: overpaymentId,
          invoice_id: targetId,
          allocation_id: allocationId,
          amount: 0,
        };
        await allocateOverpayment(overpaymentId, deallocData);
      } else {
        // For overpayment allocations, use the allocate_to_overpayment endpoint with amount=0
        const deallocData = {
          overpayment_id: overpaymentId,
          target_overpayment_id: targetId,
          allocation_id: allocationId,
          amount: 0,
        };
        await allocateOverpayment(overpaymentId, deallocData);
      }
    } else {
      // Prepare allocation data for normal allocation
      const allocationData = {
        amount: overpaymentAmount,
        partner_id: selectedPartner,
      };

      // Add the appropriate target ID based on allocation type
      if (allocationType === "invoice") {
        allocationData.invoice_id = targetId;
      } else {
        allocationData.target_overpayment_id = targetId;
      }

      // If this is updating an existing allocation, include the allocation_id
      if (allocationId) {
        allocationData.allocation_id = allocationId;
      }

      await allocateOverpayment(overpaymentId, allocationData);
    }

    // Show success notification with the correct invoice number
    if (isDeallocation) {
      // For unallocation, immediately update the UI state
      setItems((prevItems) => {
        return prevItems.map((item) => {
          if (item.id === targetId) {
            return {
              ...item,
              overpaymentAmount: 0,
            };
          }
          return item;
        });
      });

      // Update allocations array to remove this allocation
      setAllocations((prevAllocations) =>
        prevAllocations.filter((alloc) =>
          allocationType === "invoice"
            ? alloc.invoice_id !== targetId
            : alloc.target_overpayment_id !== targetId
        )
      );

      notification.success({
        message: "Overpayment Unallocated",
        description: `Successfully unallocated overpayment from ${
          allocationType === "invoice" ? "invoice" : "overpayment"
        } ${targetIdentifier}`,
      });
    } else {
      notification.success({
        message: "Overpayment Allocated",
        description: `Successfully ${
          allocationId ? "updated" : "allocated"
        } ${overpaymentAmount} to ${
          allocationType === "invoice" ? "invoice" : "overpayment"
        } ${targetIdentifier}`,
      });
    }

    // Clear cache to ensure fresh data on next page load
    clearAccountingCache();

    // Always refetch overpayment data to ensure we have the latest information
    fetchOverpayment();

    // Refresh the items list
    if (selectedPartner) {
      fetchItems(selectedPartner);
    }

    return true;
  } catch (error) {
    console.error("Error allocating overpayment:", error);

    // Provide more specific error messages
    let errorMessage = "Failed to allocate overpayment";

    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response?.status === 400) {
      errorMessage =
        "Invalid overpayment data. The amount may exceed the available overpayment amount or invoice remaining balance.";
    } else if (error.response?.status === 404) {
      errorMessage = "Invoice or overpayment not found.";
    } else if (error.response?.status === 403) {
      errorMessage = "You do not have permission to allocate this overpayment.";
    }

    notification.error({
      message: "Allocation Error",
      description: errorMessage,
    });

    return false;
  } finally {
    setLoadingAllocation(false);
  }
};

/**
 * Handles allocation of overpayment to multiple selected invoices or overpayments
 *
 * @param {string|number} overpaymentId - ID of the overpayment
 * @param {Array} selectedItemIds - Array of selected invoice or overpayment IDs
 * @param {Array} items - Current invoices or overpayments array
 * @param {Array} allocations - Current allocations array
 * @param {string|number|null} selectedPartner - ID of selected partner
 * @param {Function} setLoadingAllocation - State setter for loading indicator
 * @param {Function} handleAllocateOverpayment - Function to allocate overpayment to a single item
 * @param {string} allocationType - Type of allocation ('invoice' or 'overpayment')
 * @returns {Promise<void>}
 */
export const handleAllocateAllChecked = async (
  overpaymentId,
  selectedItemIds,
  items,
  allocations,
  selectedPartner,
  setLoadingAllocation,
  handleAllocateOverpayment,
  allocationType = "invoice"
) => {
  if (selectedItemIds.length === 0) {
    notification.warning({
      message: `No ${
        allocationType === "invoice" ? "Invoices" : "Overpayments"
      } Selected`,
      description: `Please select at least one ${
        allocationType === "invoice" ? "invoice" : "overpayment"
      } to allocate overpayment.`,
    });
    return;
  }

  setLoadingAllocation(true);

  try {
    // Process each selected item one by one
    for (const itemId of selectedItemIds) {
      const item = items.find((i) => i.id === itemId);

      if (!item) continue;

      // Skip items that are already fully paid or have no remaining balance
      if (
        parseFloat(item.remainingAmount) <= 0 ||
        (allocationType === "invoice" && item.status === "paid")
      ) {
        continue;
      }

      // Get the allocation ID if it exists
      const existingAllocation =
        allocationType === "invoice"
          ? allocations.find((alloc) => alloc.invoice_id === itemId)
          : allocations.find((alloc) => alloc.target_overpayment_id === itemId);
      const allocationId = existingAllocation ? existingAllocation.id : null;

      // Use the entered overpayment amount if available, otherwise use the remaining amount
      let amount;
      if (item.overpaymentAmount && parseFloat(item.overpaymentAmount) > 0) {
        amount = parseFloat(item.overpaymentAmount);
      } else {
        amount = parseFloat(item.remainingAmount);
      }

      // Allocate overpayment to this item
      // Define a noop function to avoid lint errors
      const noop = () => {
        /* no operation */
      };

      await handleAllocateOverpayment(
        overpaymentId,
        itemId,
        amount,
        allocations,
        items,
        allocationId,
        selectedPartner,
        setLoadingAllocation,
        noop, // Placeholder for setItems since we'll refresh after all allocations
        noop, // Placeholder for setAllocations since we'll refresh after all allocations
        noop, // Placeholder for fetchOverpayment since we'll refresh after all allocations
        noop, // Placeholder for fetchItems since we'll refresh after all allocations
        allocationType
      );
    }
  } catch (error) {
    console.error("Error in bulk allocation:", error);
    notification.error({
      message: "Bulk Allocation Error",
      description:
        "An error occurred while allocating overpayment to multiple invoices.",
    });
  } finally {
    setLoadingAllocation(false);
  }
};
